/**
 * Payroll Data Transformer
 *
 * Centralized data transformation layer for payroll wizard.
 * Converts between API formats and application domain models.
 *
 * This is the SINGLE SOURCE OF TRUTH for all payroll data transformations.
 * All conversions should happen here, never in components or composables.
 */

import { parseApiDateTime, parseApiDate, toApiDate } from '../utils/dateUtils'

// ============================================
// CONSTANTS
// ============================================

export const ADVANCE_STATUS = {
  OPEN: 'open',
  PARTIAL: 'partial',
  PAID: 'paid',
  CANCELLED: 'cancelled'
} as const
export type AdvanceStatus = typeof ADVANCE_STATUS[keyof typeof ADVANCE_STATUS]

export const ADVANCE_STATUS_LABELS: Record<AdvanceStatus, string> = {
  [ADVANCE_STATUS.OPEN]: 'Aberto',
  [ADVANCE_STATUS.PARTIAL]: 'Parcial',
  [ADVANCE_STATUS.PAID]: 'Pago',
  [ADVANCE_STATUS.CANCELLED]: 'Cancelado'
}

export const ADVANCE_STATUS_SEVERITY: Record<AdvanceStatus, string> = {
  [ADVANCE_STATUS.OPEN]: 'warning',
  [ADVANCE_STATUS.PARTIAL]: 'info',
  [ADVANCE_STATUS.PAID]: 'success',
  [ADVANCE_STATUS.CANCELLED]: 'danger'
}

export const ADVANCE_SOURCE_TYPE = {
  MANUAL: 'manual',
  CASH: 'cash'
} as const
export type AdvanceSourceType = typeof ADVANCE_SOURCE_TYPE[keyof typeof ADVANCE_SOURCE_TYPE]

export const ADVANCE_SOURCE_TYPE_LABELS: Record<AdvanceSourceType, string> = {
  [ADVANCE_SOURCE_TYPE.MANUAL]: 'Manual',
  [ADVANCE_SOURCE_TYPE.CASH]: 'Dinheiro'
}

export const ADVANCE_SOURCE_TYPE_SEVERITY: Record<AdvanceSourceType, string> = {
  [ADVANCE_SOURCE_TYPE.MANUAL]: 'info',
  [ADVANCE_SOURCE_TYPE.CASH]: 'success'
}

export const DEDUCTION_STATUS = {
  OPEN: 'open',
  PARTIAL: 'partial',
  PAID: 'paid',
  CANCELLED: 'cancelled'
} as const
export type DeductionStatus = typeof DEDUCTION_STATUS[keyof typeof DEDUCTION_STATUS]

export const DEDUCTION_STATUS_LABELS: Record<DeductionStatus, string> = {
  [DEDUCTION_STATUS.OPEN]: 'Aberto',
  [DEDUCTION_STATUS.PARTIAL]: 'Parcial',
  [DEDUCTION_STATUS.PAID]: 'Pago',
  [DEDUCTION_STATUS.CANCELLED]: 'Cancelado'
}

export const DEDUCTION_STATUS_SEVERITY: Record<DeductionStatus, string> = {
  [DEDUCTION_STATUS.OPEN]: 'warning',
  [DEDUCTION_STATUS.PARTIAL]: 'info',
  [DEDUCTION_STATUS.PAID]: 'success',
  [DEDUCTION_STATUS.CANCELLED]: 'danger'
}

export const REFUND_STATUS_LABELS: Record<string, string> = {
  '-1': 'Recusado',
  '0': 'Pendente',
  '1': 'Aceito'
}

export const REFUND_STATUS_SEVERITY: Record<string, string> = {
  '-1': 'danger',
  '0': 'warning',
  '1': 'success'
}

// ============================================
// DOMAIN TYPES
// ============================================

export interface Advance {
  request_date: Date | null
  request_date_string: string | null
  amount: number
  amount_paid: number
  amount_remaining: number
  status_label: string
  status_severity: string
  source_type: AdvanceSourceType
  source_type_label: string
  source_type_severity: string
  recebimento_id: string | number | null
  origin_period_id: string | number | null
  cash_date: Date | null
  cash_cliente_nome: string | null
  [key: string]: unknown
}

export interface Deduction {
  amount: number
  amount_paid: number
  amount_remaining: number
  status_label: string
  status_severity: string
  [key: string]: unknown
}

export interface Refund {
  expense_date: Date | null
  expense_date_string: string | null
  request_date: Date | null
  accepted_at: Date | null
  declined_at: Date | null
  created_at: Date | null
  updated_at: Date | null
  amount: number | null
  [key: string]: unknown
}

export interface CashRecebimento {
  date: Date | null
  amount: number
  [key: string]: unknown
}

export interface OpenAdvancesResponse {
  advances: Advance[]
  summary_by_employee: unknown[]
  total_open: number
  available_cash_recebimentos: unknown[]
  selected_items: unknown[]
}

export interface OpenDeductionsResponse {
  deductions: Deduction[]
  summary_by_employee: unknown[]
  total_open: number
  selected_items: unknown[]
}

export interface PendingRefundsResponse {
  refunds: Array<Record<string, unknown>>
  summary_by_user: unknown[]
  total_pending: number
}

// ============================================
// EMPLOYEE ADVANCE TRANSFORMERS
// ============================================

export function transformAdvanceFromApi(data: any): Advance | null {
  if (!data) return null

  const sourceType: AdvanceSourceType = data.source_type || ADVANCE_SOURCE_TYPE.MANUAL

  return {
    ...data,
    request_date: data.request_date ? parseApiDate(data.request_date) : null,
    request_date_string: data.request_date || null,
    amount: parseFloat(data.amount || 0),
    amount_paid: parseFloat(data.amount_paid || 0),
    amount_remaining: parseFloat(data.amount || 0) - parseFloat(data.amount_paid || 0),
    status_label: ADVANCE_STATUS_LABELS[data.status as AdvanceStatus] || 'Desconhecido',
    status_severity: ADVANCE_STATUS_SEVERITY[data.status as AdvanceStatus] || 'info',
    source_type: sourceType,
    source_type_label: data.source_type_label || ADVANCE_SOURCE_TYPE_LABELS[sourceType] || 'Manual',
    source_type_severity: ADVANCE_SOURCE_TYPE_SEVERITY[sourceType] || 'info',
    recebimento_id: data.recebimento_id || null,
    origin_period_id: data.origin_period_id || null,
    cash_date: data.cash_date ? parseApiDate(data.cash_date) : null,
    cash_cliente_nome: data.cash_cliente_nome || null
  }
}

export function transformAdvancesFromApi(advances: any): Advance[] {
  if (!Array.isArray(advances)) return []
  return advances.map(transformAdvanceFromApi).filter((a): a is Advance => a !== null)
}

export function transformAdvanceToApi(advance: any): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    employee_id: advance.employee_id,
    franchise_id: advance.franchise_id,
    title: advance.title,
    description: advance.description || null,
    amount: advance.amount
  }

  if (advance.request_date) {
    payload.request_date = advance.request_date instanceof Date
      ? toApiDate(advance.request_date)
      : advance.request_date
  }

  return payload
}

export function transformOpenAdvancesResponse(data: any): OpenAdvancesResponse {
  if (!data) {
    return {
      advances: [],
      summary_by_employee: [],
      total_open: 0,
      available_cash_recebimentos: [],
      selected_items: []
    }
  }

  return {
    advances: transformAdvancesFromApi(data.advances || []),
    summary_by_employee: data.summary_by_employee || [],
    total_open: parseFloat(data.total_open || 0),
    available_cash_recebimentos: data.available_cash_recebimentos || [],
    selected_items: data.selected_items || []
  }
}

// ============================================
// EMPLOYEE DEDUCTION TRANSFORMERS
// ============================================

export function transformDeductionFromApi(data: any): Deduction | null {
  if (!data) return null

  return {
    ...data,
    amount: parseFloat(data.amount || 0),
    amount_paid: parseFloat(data.amount_paid || 0),
    amount_remaining: parseFloat(data.amount || 0) - parseFloat(data.amount_paid || 0),
    status_label: DEDUCTION_STATUS_LABELS[data.status as DeductionStatus] || 'Desconhecido',
    status_severity: DEDUCTION_STATUS_SEVERITY[data.status as DeductionStatus] || 'info'
  }
}

export function transformDeductionsFromApi(deductions: any): Deduction[] {
  if (!Array.isArray(deductions)) return []
  return deductions.map(transformDeductionFromApi).filter((d): d is Deduction => d !== null)
}

export function transformDeductionToApi(deduction: any): Record<string, unknown> {
  return {
    employee_id: deduction.employee_id,
    franchise_id: deduction.franchise_id,
    title: deduction.title,
    description: deduction.description || null,
    amount: deduction.amount
  }
}

export function transformOpenDeductionsResponse(data: any): OpenDeductionsResponse {
  if (!data) {
    return {
      deductions: [],
      summary_by_employee: [],
      total_open: 0,
      selected_items: []
    }
  }

  return {
    deductions: transformDeductionsFromApi(data.deductions || []),
    summary_by_employee: data.summary_by_employee || [],
    total_open: parseFloat(data.total_open || 0),
    selected_items: data.selected_items || []
  }
}

// ============================================
// REFUND TRANSFORMERS
// ============================================

export function transformRefundFromApi(refund: any): Refund | null {
  if (!refund) return null

  return {
    ...refund,
    expense_date: refund.expense_date ? parseApiDate(refund.expense_date) : null,
    expense_date_string: refund.expense_date || null,
    request_date: refund.request_date ? parseApiDateTime(refund.request_date) : null,
    accepted_at: refund.accepted_at ? parseApiDateTime(refund.accepted_at) : null,
    declined_at: refund.declined_at ? parseApiDateTime(refund.declined_at) : null,
    created_at: refund.created_at ? parseApiDateTime(refund.created_at) : null,
    updated_at: refund.updated_at ? parseApiDateTime(refund.updated_at) : null,
    amount: refund.amount !== undefined ? parseFloat(refund.amount) / 100 : null
  }
}

export function transformRefundsFromApi(refunds: any): Refund[] {
  if (!Array.isArray(refunds)) return []
  return refunds.map(transformRefundFromApi).filter((r): r is Refund => r !== null)
}

export function transformRefundToApi(refundData: any): Record<string, unknown> {
  const payload: Record<string, unknown> = { ...refundData }

  if (payload.expense_date instanceof Date) {
    payload.expense_date = toApiDate(payload.expense_date)
  }

  return payload
}

export function transformPendingRefundsResponse(data: any): PendingRefundsResponse {
  if (!data) {
    return {
      refunds: [],
      summary_by_user: [],
      total_pending: 0
    }
  }

  const refunds = (data.refunds || []).map((ref: any) => ({
    ...ref,
    expense_date: ref.expense_date ? parseApiDate(ref.expense_date) : null,
    expense_date_string: ref.expense_date || null,
    request_date: ref.request_date ? parseApiDateTime(ref.request_date) : null,
    created_at: ref.created_at ? parseApiDateTime(ref.created_at) : null
  }))

  return {
    refunds,
    summary_by_user: data.summary_by_user || [],
    total_pending: data.total_pending || 0
  }
}

// ============================================
// CASH RECEBIMENTO TRANSFORMERS
// ============================================

export function transformCashRecebimentoFromApi(data: any): CashRecebimento | null {
  if (!data) return null

  return {
    ...data,
    date: data.date ? parseApiDate(data.date) : null,
    amount: parseFloat(data.amount || 0)
  }
}

export function transformCashRecebimentosFromApi(recebimentos: any): CashRecebimento[] {
  if (!Array.isArray(recebimentos)) return []
  return recebimentos.map(transformCashRecebimentoFromApi).filter((r): r is CashRecebimento => r !== null)
}

// ============================================
// PAYROLL PERIOD TRANSFORMERS
// ============================================

export function transformPeriodFromApi(data: any): Record<string, unknown> | null {
  if (!data) return null

  return {
    ...data,
    start_date: data.start_date ? parseApiDate(data.start_date) : null,
    end_date: data.end_date ? parseApiDate(data.end_date) : null,
    reference_date: data.reference_date ? parseApiDate(data.reference_date) : null
  }
}

// ============================================
// UTILITY TRANSFORMERS
// ============================================

export function transformSelectedDiscountsToApi(
  selectedItems: any,
  idField: 'advance_id' | 'deduction_id' = 'advance_id'
): Array<Record<string, unknown>> {
  if (!Array.isArray(selectedItems)) return []

  return selectedItems.map((item: any) => ({
    [idField]: String(item[idField] || item.id),
    amount: parseFloat(item.amount || 0)
  }))
}

export function transformRefundDecisionsToApi(decisions: any): Array<Record<string, unknown>> {
  if (!Array.isArray(decisions)) return []

  return decisions.map((decision: any) => ({
    refund_id: decision.refund_id,
    action: decision.action,
    categoria_id: decision.categoria_id || null,
    reason: decision.reason || null
  }))
}

export function getRefundStatusLabel(status: number | string): string {
  return REFUND_STATUS_LABELS[String(status)] || 'Desconhecido'
}

export function getRefundStatusSeverity(status: number | string): string {
  return REFUND_STATUS_SEVERITY[String(status)] || 'secondary'
}

// ============================================
// EXPORT ALL TRANSFORMERS
// ============================================

export default {
  ADVANCE_STATUS,
  ADVANCE_STATUS_LABELS,
  ADVANCE_STATUS_SEVERITY,
  ADVANCE_SOURCE_TYPE,
  ADVANCE_SOURCE_TYPE_LABELS,
  ADVANCE_SOURCE_TYPE_SEVERITY,
  DEDUCTION_STATUS,
  DEDUCTION_STATUS_LABELS,
  DEDUCTION_STATUS_SEVERITY,

  transformAdvanceFromApi,
  transformAdvancesFromApi,
  transformAdvanceToApi,
  transformOpenAdvancesResponse,

  transformDeductionFromApi,
  transformDeductionsFromApi,
  transformDeductionToApi,
  transformOpenDeductionsResponse,

  transformRefundFromApi,
  transformRefundsFromApi,
  transformRefundToApi,
  transformPendingRefundsResponse,
  getRefundStatusLabel,
  getRefundStatusSeverity,

  transformCashRecebimentoFromApi,
  transformCashRecebimentosFromApi,

  transformPeriodFromApi,

  transformSelectedDiscountsToApi,
  transformRefundDecisionsToApi
}
